// "Sign in with Google": loads Google's button, sends the result to the
// server (which checks it and looks the email up in the household), and
// stores the session token it returns in place of the household key.

import { useEffect, useState } from 'react';
import { apiUrl } from './api.ts';
import { setDevicePrefs } from './device.ts';
import { reconnect } from './index.ts';

interface GoogleId {
  initialize(o: Record<string, unknown>): void;
  renderButton(el: HTMLElement, o: Record<string, unknown>): void;
  disableAutoSelect(): void;
}
export interface TokenClient {
  requestAccessToken(o?: Record<string, unknown>): void;
}
interface GoogleOAuth2 {
  initTokenClient(o: Record<string, unknown>): TokenClient;
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleId; oauth2: GoogleOAuth2 } };
  }
}

let config: Promise<string | null> | undefined;
/** The server's Google client id, or null when Google sign-in isn't set up. */
export function googleClientId(): Promise<string | null> {
  if (!config) {
    const p = fetch(apiUrl('/api/auth/config'), { cache: 'no-store' })
      .then((r) => (r.ok ? (r.json() as Promise<{ google?: string | null }>) : { google: null }))
      .then((j) => j.google || null)
      .catch(() => {
        config = undefined; // offline: ask again next time
        return null;
      });
    config = p;
    return p;
  }
  return config;
}

export function useGoogleClientId(): string | null | undefined {
  const [id, setId] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void googleClientId().then((v) => live && setId(v));
    return () => {
      live = false;
    };
  }, []);
  return id;
}

/**
 * Google refuses to sign in inside embedded browsers (Android WebViews such as
 * the Scalefusion kiosk browser, and in-app browsers) — use the key there.
 */
export const googleBlockedHere = (): boolean => /; wv\)|FBAN|FBAV|Instagram|Line\//.test(navigator.userAgent);

let script: Promise<GoogleId> | undefined;
function loadGis(): Promise<GoogleId> {
  script ??= new Promise<GoogleId>((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google.accounts.id);
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => (window.google?.accounts?.id ? resolve(window.google.accounts.id) : reject(new Error('Google sign-in didn’t load')));
    s.onerror = () => {
      script = undefined;
      reject(new Error('Couldn’t load Google sign-in — are you online?'));
    };
    document.head.append(s);
  });
  return script;
}

/** Google's OAuth token client (same script as the sign-in button). */
export async function googleOAuth(): Promise<GoogleOAuth2> {
  await loadGis();
  const o = window.google?.accounts?.oauth2;
  if (!o) throw new Error('Google sign-in didn’t load');
  return o;
}

let onCredential: ((credential: string) => void) | undefined;
let initialisedFor: string | undefined;

/** Draw Google's button into `el`; `cb` gets the ID token when someone signs in. */
export async function renderGoogleButton(el: HTMLElement, clientId: string, cb: (credential: string) => void, width: number): Promise<void> {
  const gis = await loadGis();
  onCredential = cb;
  if (initialisedFor !== clientId) {
    gis.initialize({
      client_id: clientId,
      callback: (r: { credential?: string }) => r.credential && onCredential?.(r.credential),
      ux_mode: 'popup',
      auto_select: false,
      itp_support: true,
      use_fedcm_for_button: true,
    });
    initialisedFor = clientId;
  }
  const dark = document.documentElement.dataset.theme === 'dark' || (document.documentElement.dataset.theme !== 'light' && matchMedia('(prefers-color-scheme: dark)').matches);
  gis.renderButton(el, { type: 'standard', theme: dark ? 'filled_black' : 'outline', size: 'large', text: 'signin_with', shape: 'pill', logo_alignment: 'left', width: Math.min(400, Math.max(200, Math.round(width))) });
}

export interface SignedIn {
  token: string;
  personId: string;
  name: string;
  email: string;
}

/** Swap Google's ID token for an Arlo HQ session, and connect with it. */
export async function completeGoogleSignIn(credential: string): Promise<SignedIn> {
  const res = await fetch(apiUrl('/api/auth/google'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ credential }) });
  const j = (await res.json().catch(() => ({}))) as Partial<SignedIn> & { error?: string };
  if (!res.ok || !j.token || !j.personId) throw new Error(j.error ?? `Sign-in failed (${res.status})`);
  setDevicePrefs({ key: j.token, personId: j.personId, account: { email: j.email ?? '', name: j.name }, guest: undefined });
  await reconnect();
  return j as SignedIn;
}

/** Forget the Google session on this device. */
export async function signOutGoogle(): Promise<void> {
  setDevicePrefs({ key: undefined, account: undefined });
  try {
    window.google?.accounts.id.disableAutoSelect();
  } catch {
    /* not loaded */
  }
  await reconnect();
}
